import PropTypes from 'prop-types';
import React from 'react';
import Dialog from 'material-ui/Dialog';
import Dropzone from 'react-dropzone';
import FlatButton from 'material-ui/FlatButton';
import LinearProgress from 'material-ui/LinearProgress';
import Snackbar from 'material-ui/Snackbar';

import ConstValues from 'utils/const';
import FileUploadNewCallActions from 'actions/file-upload/FileUploadNewCallActions';
import Button from '@material-ui/core/Button';
import {ThemeProvider} from '@components/@theme';
import {lightTheme} from '@components/@theme/lightTheme';
import CheckIcon from 'material-ui/svg-icons/navigation/check';
import StringInputBox from 'components/pure/utils/StringInputBox';
import Theme from 'styles/theme';
import Checkbox from 'material-ui/Checkbox';
import 'styles/calls/FileUploadNewCallDialog.styl';

import FileUploadErrorSizeSVG from 'images/common/FileUploadErrorSize.svg';
import {UploadIcon} from '@components/icon/UploadIcon';
import {XMarkLargeIcon} from '@components/icon/XMarkLargeIcon';
import {getMuiV4Theme} from 'utils/css/themeUtils';

const dialogContentStyle = {
  maxWidth: '475px',
  width: '55%',
};
const fileUploadLabelStyle = {
  color: '#646464',
  fontSize: '20px',
  textTransform: 'none',
};
const fileUploadStatusButtonStyle = {
  display: 'inherit',
  height: '100px',
  margin: '0 auto',
  width: 'auto',
};
const browseButtonStyle = {
  color: ConstValues.linkButtonColor,
  minWidth: '0px',
  padding: '0px',
};
const okButtonStyle = {
  marginLeft: '10%',
  width: '80%',
};

/**
 * <FileUploadNewCall /> renders a ReactJS <Dialog /> component that is a modal dialog
 * which allows a user to drag-n-drop a file and upload it onto the backend server for processing
 */
class FileUploadNewCallDialog extends React.Component {
  static propTypes = {
    onTouchTapCloseButton: PropTypes.func.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      contact: '',
      openErrorSnackbar: false,
      title: '',
    };
    this.onChangeTitle = this.onChangeTitle.bind(this);
    this.onChangeContact = this.onChangeContact.bind(this);
    this.onDropFiles = this.onDropFiles.bind(this);
    this.onHandleSnackbarClose = this.onHandleSnackbarClose.bind(this);
    this.onTouchTapSubmitButton = this.onTouchTapSubmitButton.bind(this);
    this.onTouchTapUploadErrorOkButton = this.onTouchTapUploadErrorOkButton.bind(this);
  }

  /**
   * Whenever the <FileUploadNewCallDialog /> component gets closed, we reset its `state`
   * so that the next time it opens, the user will see a clean slate
   * @param  {object} nextProps New properties this component will receive
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (typeof nextProps.open !== 'undefined' && !nextProps.open) {
      this.resetState();
    }
  }

  /** Reset `this.state` using the initial values */
  resetState() {
    this.setState({
      contact: '',
      title: '',
      isSingleSpeakerCall: false,
      openErrorSnackbar: false,
      rejectedFile: false,
    });
  }

  /**
   * Start the file upload process and display the upload progress bar as well as the "Call title" and "Contact"
   * form upload fields.
   *
   * In this case we only care about one file at a time.
   *
   * @param  {array} acceptedFiles An array of accepted file objects.
   * @param  {array} rejectedFiles An array of rejected file objects.
   */
  onDropFiles(acceptedFiles, rejectedFiles) {
    if (acceptedFiles.length > 0) {
      setImmediate(() => {
        // Mark the progress as larger than 0 so that the progress bar shows up immediately
        FileUploadNewCallActions.uploadFileInProgress(ConstValues.minFileUploadProgress);
      });
      // upload accepted file
      const file = acceptedFiles[0];
      setImmediate(() => {
        FileUploadNewCallActions.uploadFile(file);
      });
    } else if (rejectedFiles.length > 0) {
      // show error because of rejected file
      this.setState({
        openErrorSnackbar: true,
        rejectedFile: true,
      });
    }
  }

  /**
   * Render a <dropzone /> component inside the file upload <Dialog /> component
   */
  renderFileUploadDropzone() {
    return (
      <div className="dialog-container file-dropzone">
        <Dropzone
          className={`dropzone`}
          onDrop={this.onDropFiles}
          accept={ConstValues.allowedMimeTypes.join(`,`)}
          multiple={false}>
          <UploadIcon />
          <div className={`upload-label`}>
            <span>Drop your call here</span>
          </div>
          <div className={`browse-label`}>
            <span>
              {/* TODO EV-18732 update button usage */}
              or <FlatButton style={browseButtonStyle}>Browse</FlatButton> in your computer
            </span>
          </div>

          <div className="single-speaker-checkbox">
            <Checkbox
              label="Skip speaker identification (call contains single speaker)"
              checked={this.state.isSingleSpeakerCall}
              onCheck={(event, isChecked) => this.setState({isSingleSpeakerCall: isChecked})}
              onClick={event => {
                event.stopPropagation();
              }}
            />
          </div>
        </Dropzone>
      </div>
    );
  }

  /**
   * Return the label used in <FlatButton /> to represent the current status of the upload
   * @return {string} Status of the current upload which can be either "Uploading..." or "Uploaded"
   */
  uploadProgressLabel() {
    if (this.props.uploadProgress < 1.0 || !this.props.uploadId) {
      return 'Uploading...';
    } else {
      return 'Uploaded';
    }
  }

  /**
   * Return true when the metadata is full and the upload is complete
   * @return {Boolean} true if metadata is full and the upload is complete; false otherwise
   */
  isMetaDataValid() {
    const state = this.state;
    return (
      state.title.trim().length > 0 &&
      state.contact.trim().length > 0 &&
      this.props.uploadProgress >= 1.0 &&
      this.props.uploadId
    );
  }

  /**
   * Return percentage of file upload progress percentage
   * @return {Number} [0, 100]
   */
  getProgressPercentage() {
    // the percentage should only reach 100% if the upload id was returned
    const max = this.props.uploadId ? 100 : 95;
    return this.props.uploadProgress * max;
  }

  /**
   * Callback when the contact is changed in one of the <StringInputBox /> component
   * @param  {string} contact A new contact
   */
  onChangeContact(contact) {
    this.setState({contact: contact});
  }

  /**
   * Callback when the title is changed in one of the <StringInputBox /> component
   * @param  {string} title A new title
   */
  onChangeTitle(title) {
    this.setState({title: title});
  }

  /** Callback when the <Snackbar /> closes */
  onHandleSnackbarClose() {
    if (this.props.uploadError) {
      FileUploadNewCallActions.clearUploadErrorAndProgress();
    } else {
      this.setState({
        openErrorSnackbar: false,
        rejectedFile: false,
      });
    }
  }

  /** Callback when the user clicks the <GenericFlatSubmitButton /> */
  onTouchTapSubmitButton() {
    if (this.isMetaDataValid()) {
      FileUploadNewCallActions.updateFileUploadMetadata(
        this.props.uploadId,
        this.state.title,
        this.state.contact,
        this.props.uploadFilename,
        this.state.isSingleSpeakerCall
      );
    } else {
      this.setState({openErrorSnackbar: true});
    }
  }

  /**
   * Callback triggered when the user clicks the `OK` button. When this happens, we should clear the upload error
   * as well as progress from `FileUploadNewCallStore` so that the user will be able to see the default upload dropzone
   * again
   */
  onTouchTapUploadErrorOkButton() {
    FileUploadNewCallActions.clearUploadErrorAndProgress();
  }

  /**
   * Render `this.props.uploadError` and an `OK` button, clicking which should render the default dropzone dialog
   * @return {JSX} A simple text div and and a flat "OK" button
   */
  renderUploadErrorMessageAndButton() {
    return (
      <ThemeProvider theme={lightTheme}>
        <div className="dialog-container upload-error">
          <img alt="Error" className="error-image" src={FileUploadErrorSizeSVG} />
          <div className="message">
            <span>{this.props.uploadError}</span>
          </div>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            style={okButtonStyle}
            onClick={this.onTouchTapUploadErrorOkButton}>
            OK
          </Button>
        </div>
      </ThemeProvider>
    );
  }

  /**
   * Render success message and an `OK` button, clicking which should close the dialog
   * @return {JSX} A simple text div and and a flat "OK" button
   */
  renderFileUploadSuccessMessage() {
    return (
      <ThemeProvider theme={lightTheme}>
        <div className="dialog-container upload-success">
          <div className="message">
            <span>
              Your file has been uploaded into the system, and is queued for processing. Once it has
              been virus scanned and converted, it will appear in your calls list, and you'll
              receive an email acknowledgement.
            </span>
          </div>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            style={okButtonStyle}
            onClick={this.props.onTouchTapCloseButton}>
            OK
          </Button>
        </div>
      </ThemeProvider>
    );
  }

  /**
   * Render a <form /> component inside the file upload <Dialog /> component that allows the user
   * to update the metadata for a uploaded file
   * @return {JSX} A form-looking component that allows the user to provide metadata for a custom file upload
   */
  renderFileUploadMetadataForm() {
    return (
      <ThemeProvider theme={lightTheme}>
        <div className="dialog-container">
          <FlatButton
            label={this.uploadProgressLabel()}
            style={fileUploadStatusButtonStyle}
            className={`file-upload-status-icon`}
            labelStyle={fileUploadLabelStyle}
            icon={<UploadIcon fill={Theme.palette.darkGrey} />}
          />
          <LinearProgress
            value={this.getProgressPercentage()}
            mode={`determinate`}
            color={getMuiV4Theme().palette.primary.main}
          />
          <span className="file-upload-name-label">{this.props.uploadFilename}</span>
          <StringInputBox
            className={`file-upload-string-input`}
            placeholder={`Call title`}
            value={this.state.title}
            onChange={this.onChangeTitle}
          />
          <StringInputBox
            className={`file-upload-string-input`}
            placeholder={`Contact`}
            value={this.state.contact}
            onChange={this.onChangeContact}
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className="file-upload-submit-button"
            onClick={this.onTouchTapSubmitButton}>
            <CheckIcon color="white" />
          </Button>
        </div>
      </ThemeProvider>
    );
  }

  /**
   * Return the error message for <Snackbar /> depending on which metadata field is missing
   * @return {string} An error message
   */
  snackbarErrorMessage() {
    if (this.state.rejectedFile) {
      return `File type can't be handled`;
    } else if (
      this.props.uploadProgress > 0.0 &&
      (this.state.title.trim().length === 0 || this.state.contact.trim().length === 0)
    ) {
      return `Please fill in both "Call title" and "Contact"`;
    } else if (this.props.uploadProgress < 1.0 || !this.props.uploadId) {
      return `Please wait until the upload completes`;
    }
    return null;
  }

  render() {
    let headerTitle = `Add New Call`;
    if (this.props.uploadSuccess) {
      headerTitle = `Your File Is Being Processed`;
    }

    return (
      <Dialog
        open={this.props.open}
        autoScrollBodyContent={true}
        contentStyle={dialogContentStyle}
        title={headerTitle}
        onRequestClose={this.props.onTouchTapCloseButton}>
        <XMarkLargeIcon
          tooltip="Close"
          className="dialog-close-button"
          onClick={this.props.onTouchTapCloseButton}
        />
        {(() => {
          if (this.props.uploadError) {
            return this.renderUploadErrorMessageAndButton();
          } else if (this.props.uploadSuccess) {
            return this.renderFileUploadSuccessMessage();
          } else if (this.props.uploadProgress > 0.0) {
            return this.renderFileUploadMetadataForm();
          } else {
            return this.renderFileUploadDropzone();
          }
        })()}
        <Snackbar
          open={this.state.openErrorSnackbar}
          message={this.snackbarErrorMessage()}
          autoHideDuration={3000}
          onRequestClose={this.onHandleSnackbarClose}
        />
      </Dialog>
    );
  }
}

export default FileUploadNewCallDialog;
